// lib/leasing/country-continent.ts
//
// Country (ISO-3166 alpha-2) → continent bucket, for the tenants folder browser (Region → Country).
// Country display names + flags are derived at runtime (Intl.DisplayNames + regional-indicator
// emoji), so this only carries the continent grouping + a normalizer for the few non-ISO values in
// the data (full names like 'FRANCE').

export type Continent = 'Europe' | 'North America' | 'South America' | 'Asia' | 'Africa' | 'Oceania' | 'Other';

export const CONTINENT_ORDER: Continent[] = ['Europe', 'North America', 'South America', 'Asia', 'Africa', 'Oceania', 'Other'];

const EUROPE = 'AD AL AT BA BE BG BY CH CY CZ DE DK EE ES FI FO FR GB GG GI GR HR HU IE IM IS IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS RU SE SI SK SM UA VA XK';
const NORTH_AMERICA = 'AG AI AW BB BL BM BQ BS BZ CA CR CU CW DM DO GD GL GP GT HN HT JM KN KY LC MF MQ MS MX NI PA PR SV SX TC TT US VC VG VI';
const SOUTH_AMERICA = 'AR BO BR CL CO EC FK GF GY PE PY SR UY VE';
const ASIA = 'AE AF AM AZ BD BH BN BT CN GE HK ID IL IN IQ IR JO JP KG KH KP KR KW KZ LA LB LK MM MN MO MV MY NP OM PH PK PS QA SA SG SY TH TJ TM TR TW UZ VN YE';
const AFRICA = 'AO BF BI BJ BW CD CF CG CI CM CV DJ DZ EG EH ER ET GA GH GM GN GQ GW KE KM LR LS LY MA MG ML MR MU MW MZ NA NE NG RE RW SC SD SL SN SO SS ST SZ TD TG TN TZ UG YT ZA ZM ZW';
const OCEANIA = 'AS AU CK FJ FM GU KI MH MP NC NF NR NU NZ PF PG PW SB TK TL TO TV UM VU WF WS';

const build = (codes: string, continent: Continent, out: Record<string, Continent>) => {
  for (const c of codes.trim().split(/\s+/)) out[c] = continent;
  return out;
};

const COUNTRY_CONTINENT: Record<string, Continent> = (() => {
  const m: Record<string, Continent> = {};
  build(EUROPE, 'Europe', m);
  build(NORTH_AMERICA, 'North America', m);
  build(SOUTH_AMERICA, 'South America', m);
  build(ASIA, 'Asia', m);
  build(AFRICA, 'Africa', m);
  build(OCEANIA, 'Oceania', m);
  return m;
})();

/** Full-name / stray values in the data → ISO-2. Most rows are already ISO-2. */
const NAME_TO_ISO: Record<string, string> = {
  FRANCE: 'FR', 'UNITED KINGDOM': 'GB', UK: 'GB', 'GREAT BRITAIN': 'GB', ENGLAND: 'GB',
  GERMANY: 'DE', SPAIN: 'ES', ITALY: 'IT', 'UNITED STATES': 'US', USA: 'US', 'UNITED STATES OF AMERICA': 'US',
  BELGIUM: 'BE', NETHERLANDS: 'NL', POLAND: 'PL', SWEDEN: 'SE', SWITZERLAND: 'CH', AUSTRIA: 'AT',
  PORTUGAL: 'PT', DENMARK: 'DK', NORWAY: 'NO', IRELAND: 'IE', LUXEMBOURG: 'LU', CANADA: 'CA', 'CZECH REPUBLIC': 'CZ',
  CHINA: 'CN', JAPAN: 'JP', ROMANIA: 'RO', TURKEY: 'TR', RUSSIA: 'RU', GREECE: 'GR', FINLAND: 'FI', MOROCCO: 'MA',
};

/** Normalize a raw country value to an uppercase ISO-2 code (or '' if unusable). */
export function normalizeCountryCode(raw: string | null | undefined): string {
  const s = (raw ?? '').trim().toUpperCase();
  if (!s) return '';
  if (s.length === 2 && /^[A-Z]{2}$/.test(s)) return s;
  return NAME_TO_ISO[s] ?? '';
}

export function continentOf(iso2: string): Continent {
  return COUNTRY_CONTINENT[iso2] ?? 'Other';
}

/** Every stored form that means this ISO country (the code + any full-name aliases in the data). */
export function countryAliases(iso2: string): string[] {
  const names = Object.entries(NAME_TO_ISO).filter(([, v]) => v === iso2).map(([k]) => k);
  return [iso2, ...names];
}
